'use client';

import { c as _c } from "react/compiler-runtime";
import './horizontal-scroll-area.scss';
import { type CSSProperties, type ComponentPropsWithoutRef, type FC, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { ButtonStyle } from '../button-style/button-style.tsx';
import { HORIZONTAL_SCROLL_CONTROL_ATTRIBUTE, useHorizontalScrollArea } from './horizontal-scroll-area.logic.ts';
const horizontalScrollControlMarker = {
  [HORIZONTAL_SCROLL_CONTROL_ATTRIBUTE]: 'true'
};
export type HorizontalScrollAreaProps = Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'style' | 'className'> & {
  /** The existing horizontal scroll viewport. */
  children: ReactElement;
  /** The surface color that the overflow indicators blend into. */
  indicatorColor?: CSSProperties['backgroundColor'];
};

/**
 * Adds design system UI indicators for horizontal scroll containers.
 */
export const HorizontalScrollArea: FC<HorizontalScrollAreaProps> = t0 => {
  const $ = _c(33);
  let children;
  let indicatorColor;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      indicatorColor,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = indicatorColor;
    $[3] = props;
  } else {
    children = $[1];
    indicatorColor = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["core"];
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    canScrollEnd,
    canScrollStart,
    scrollEnd,
    scrollbarHeight,
    scrollStart,
    wrapperRef
  } = useHorizontalScrollArea();
  const t2 = canScrollEnd ? "true" : undefined;
  const t3 = canScrollStart ? "true" : undefined;
  const t4 = `${scrollbarHeight}px`;
  let t5;
  if ($[5] !== indicatorColor || $[6] !== t4) {
    t5 = {
      "--h-scroll-area-fade-color": indicatorColor,
      "--h-scroll-area-scrollbar-height": t4
    };
    $[5] = indicatorColor;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = t5 as CSSProperties;
  const t7 = !canScrollStart || undefined;
  let t8;
  if ($[8] !== t) {
    t8 = t("core:horizontalScrollArea.previousButtonAriaLabel");
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = canScrollStart ? undefined : -1;
  const t10 = canScrollStart ? scrollStart : undefined;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ArkIcon className="HorizontalScrollArea__control-icon" iconName="arrow-left" />;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t10 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t12 = <ButtonStyle className="HorizontalScrollArea__control HorizontalScrollArea__control--start" iconOnly={true} size="sm" variant="tertiary"><button {...horizontalScrollControlMarker} type="button" aria-disabled={t7} aria-label={t8} tabIndex={t9} onClick={t10}>{t11}</button></ButtonStyle>;
    $[11] = t10;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = !canScrollEnd || undefined;
  let t14;
  if ($[16] !== t) {
    t14 = t("core:horizontalScrollArea.nextButtonAriaLabel");
    $[16] = t;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = canScrollEnd ? undefined : -1;
  const t16 = canScrollEnd ? scrollEnd : undefined;
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ArkIcon className="HorizontalScrollArea__control-icon" iconName="arrow-right" />;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t13 || $[20] !== t14 || $[21] !== t15 || $[22] !== t16) {
    t18 = <ButtonStyle className="HorizontalScrollArea__control HorizontalScrollArea__control--end" iconOnly={true} size="sm" variant="tertiary"><button {...horizontalScrollControlMarker} type="button" aria-disabled={t13} aria-label={t14} tabIndex={t15} onClick={t16}>{t17}</button></ButtonStyle>;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== children || $[25] !== props || $[26] !== t12 || $[27] !== t18 || $[28] !== t2 || $[29] !== t3 || $[30] !== t6 || $[31] !== wrapperRef) {
    t19 = <div {...props} ref={wrapperRef} className="HorizontalScrollArea" data-overflow-end={t2} data-overflow-start={t3} style={t6}>{t12}{children}{t18}</div>;
    $[24] = children;
    $[25] = props;
    $[26] = t12;
    $[27] = t18;
    $[28] = t2;
    $[29] = t3;
    $[30] = t6;
    $[31] = wrapperRef;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  return t19;
};